import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OnlineConsultResponse } from "../types/api";

export interface VideoConsultState {
  incomingConsults: OnlineConsultResponse[];
  loadingConsults: boolean;
  consultsFailed: boolean;
  playRingtone: boolean;
  lastFetchTime: number | null;
  // The consult whose call runs in the floating PiP panel. Plain JSON only —
  // the DailyCall frame instance lives in a ref inside FloatingVideoPanel,
  // never in the store. In-memory only, never persisted.
  activeConsult: OnlineConsultResponse | null;
}

const initialState: VideoConsultState = {
  incomingConsults: [],
  loadingConsults: false,
  consultsFailed: false,
  playRingtone: false,
  lastFetchTime: null,
  activeConsult: null,
};

const videoConsultSlice = createSlice({
  name: "videoConsult",
  initialState: initialState,
  reducers: {
    fetchIncomingConsultsStart: (state) => {
      state.loadingConsults = true;
      state.consultsFailed = false;
    },

    fetchIncomingConsultsSuccess: (
      state,
      action: PayloadAction<OnlineConsultResponse[]>
    ) => {
      const newConsults = action.payload;
      const previousConsultIds = new Set(
        state.incomingConsults.map((c) => c.id)
      );
      const hasNewConsult = newConsults.some(
        (consult) => !previousConsultIds.has(consult.id)
      );

      state.incomingConsults = newConsults;
      state.loadingConsults = false;
      state.consultsFailed = false;
      state.lastFetchTime = Date.now();

      // Trigger ringtone if new consultation appeared
      if (hasNewConsult && newConsults.length > 0) {
        state.playRingtone = true;
      }
    },

    fetchIncomingConsultsFailed: (state, action: PayloadAction<string>) => {
      state.loadingConsults = false;
      state.consultsFailed = true;
      console.error("Video consult fetch failed:", action.payload);
    },

    stopRingtone: (state) => {
      state.playRingtone = false;
    },

    clearIncomingConsults: (state) => {
      state.incomingConsults = [];
      state.playRingtone = false;
      state.lastFetchTime = null;
      state.loadingConsults = false;
    },

    setActiveConsult: (state, action: PayloadAction<OnlineConsultResponse>) => {
      state.activeConsult = action.payload;
      state.playRingtone = false;
    },

    clearActiveConsult: (state) => {
      state.activeConsult = null;
    },

    // Guarded variant for async completions (Daily promise callbacks and
    // event handlers): a late settlement from a torn-down panel instance must
    // never clear a NEWER live call, so it clears only when the given consult
    // is still the active one.
    clearActiveConsultIfId: (state, action: PayloadAction<number>) => {
      if (state.activeConsult?.id === action.payload) {
        state.activeConsult = null;
      }
    },

    // Cross-tab suppression: a sibling tab just joined this consult (its
    // markJoined write fired our `storage` event), so drop it from the
    // incoming list immediately instead of waiting up to one poll interval.
    // The ringtone stops only when nothing is left to ring for.
    suppressIncomingConsult: (state, action: PayloadAction<number>) => {
      const next = state.incomingConsults.filter(
        (consult) => consult.id !== action.payload
      );
      if (next.length !== state.incomingConsults.length) {
        state.incomingConsults = next;
        if (next.length === 0) {
          state.playRingtone = false;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Full reset on logout (auth slice action type = "auth/logout") — the
    // incoming consults carry caller PII (name, email), so nothing may
    // survive an auth boundary, and a re-login in the same page session must
    // not resurrect a stale activeConsult.
    builder.addCase("auth/logout", () => initialState);
  },
});

export const videoConsultActions = videoConsultSlice.actions;

export default videoConsultSlice;
